import { assign, createMachine } from 'xstate';
import { setError } from '~/machines/utils/actions';
import { contextCreator } from '~/machines/utils/context';
import { LDClient, LDFlags } from '~/providers';
import {
  createPaymentHold,
  listenForPayment,
  updatePlanPaymentMethod,
  fetchExistingPaymentMethods,
  fetchStripeToken,
  sendInstantAlert,
  updateConsolidatedUtilityAccount,
} from '~/services';
import { PaymentMethod } from '~/typings/context';
import {
  allowConsolidatedUtilityAccount,
  getUserIpFromStore,
  sendToSegment,
  trackError,
} from '~/utils/tracking';
import {
  BillingContext,
  BillingDoneData,
  BillingEvent,
  BillingServices,
  BillingStates,
} from './types';

export const billingMachine = () =>
  createMachine(
    {
      tsTypes: {} as import('./machine.typegen').Typegen0,
      predictableActionArguments: true,
      schema: {
        context: {} as BillingContext,
        events: {} as BillingEvent,
        services: {} as BillingServices,
      },
      context: {
        allowMicroDeposits: false,
        billingOptions: ['acb'],
        funnelPacket: {} as BillingContext['funnelPacket'],
        errors: {},
        existingPaymentMethods: [],
        placeCreditCardHold: false,
      },
      initial: 'fetching',
      entry: 'trackStarted',
      states: {
        fetching: {
          id: 'fetching',
          invoke: {
            id: 'fetchInitialData',
            src: 'fetchInitialData',
            onDone: {
              target: 'payment',
              actions: 'setInitialData',
            },
            onError: {
              target: 'payment',
              actions: 'setInitialDataError',
            },
          },
        },
        payment: {
          id: 'payment',
          entry: 'setInitialBillingSelection',
          initial: 'idle',
          states: {
            idle: {
              on: {
                SUBMIT_NEW_SETUP_INTENT: [
                  {
                    cond: 'shouldPlaceHoldOnCreditCard',
                    target: '#hold',
                    actions: 'setPaymentType',
                  },
                  {
                    target: '#completed',
                    actions: [
                      'setPartialSelectedPaymentMethod',
                      'listenForPayment',
                      'setPaymentType',
                    ],
                  },
                ],
                SUBMIT_EXISTING_PAYMENT_METHOD: [
                  {
                    cond: 'shouldPlaceHoldOnExistingCreditCard',
                    target: '#hold.holding',
                    actions: 'saveExistingPayment',
                  },
                  {
                    target: 'submittingExisting',
                    actions: 'saveExistingPayment',
                  },
                ],
                SKIP_PAYMENT: {
                  target: '#completed',
                  actions: 'selectOption',
                },
                SELECTED_BILLING_OPTION: [
                  { cond: 'shouldSkipPayment', target: '#completed', actions: 'selectOption' },
                  { actions: 'selectOption' },
                ],
              },
            },
            submittingExisting: {
              entry: 'clearPaymentError',
              invoke: {
                src: 'selectPayment',
                onDone: {
                  target: '#completed',
                  actions: ['setSelectedPaymentMethod', 'trackPaymentVerification'],
                },
                onError: { target: 'idle', actions: 'setPaymentError' },
              },
            },
          },
        },
        hold: {
          id: 'hold',
          initial: 'waiting',
          states: {
            waiting: {
              after: {
                LISTEN_FOR_PAYMENT_TIMEOUT: 'timeout',
              },
              invoke: {
                id: 'listenForPayment',
                src: 'listenForPayment',
                onDone: {
                  target: 'holding',
                  actions: 'setSelectedPaymentMethod',
                },
                onError: 'error',
              },
            },
            holding: {
              invoke: {
                id: 'placeHold',
                src: 'placeHold',
                onDone: [
                  {
                    cond: 'paymentHoldFailed',
                    target: 'error',
                    actions: ['setPaymentHoldResult', 'handlePaymentHoldError'],
                  },
                  {
                    target: 'success',
                    actions: ['setPaymentHoldResult', 'trackPaymentVerification'],
                  },
                ],
                onError: 'error',
              },
            },
            success: {
              invoke: {
                id: 'trackPaymentSuccess',
                src: 'trackPaymentSuccess',
                onError: 'error',
              },
              on: {
                ACKNOWLEDGE_VERIFICATION: '#completed',
              },
            },
            timeout: {
              on: {
                ACKNOWLEDGE_VERIFICATION: '#completed',
                BACK_TO_PAYMENT: '#fetching',
              },
            },
            error: {
              on: {
                BACK_TO_PAYMENT: '#fetching',
              },
            },
          },
        },
        completed: {
          id: 'completed',
          type: 'final',
          entry: 'trackCompleted',
          data: (context): BillingDoneData => ({
            billingSelection: context.billingSelection!,
            selectedPaymentMethod: context.selectedPaymentMethod,
          }),
        },
      },
      on: {
        UPDATED_CONTEXT_FROM_PARENT: { actions: 'setUpdatedContextFromParent' },
      },
    },
    {
      /* eslint-disable @typescript-eslint/no-unused-vars */
      // TLDR: need the unused _context - https://xstate.js.org/docs/guides/typescript.html#troubleshooting
      actions: {
        clearPaymentError: assign(context => ({
          errors: { ...context.errors, payment: undefined },
        })),
        /*
          this is a fire-and-forget action, meaning we're not waiting for it to resolve (the machine will set this up and move on)
          we don't know when or if the listenForPayment function will return a value (since it's a GQL subscription)
          but if it does, we'll update the plan and track via segment
        */
        listenForPayment: async context => {
          const { paymentMethod } = await listenForPayment({
            accountId: context.funnelPacket.account.id,
          });
          updatePlanPaymentMethod({
            customPlanId: context.funnelPacket.customPlan.id,
            paymentMethodId: paymentMethod.id,
          });
          sendToSegment({
            type: 'payment_method_created',
            funnelPacket: context.funnelPacket,
          });
          /**
           * For ACH payment methods, we want to send an instant alert directly
           * without the payment hold step.
           */
          if (paymentMethod && paymentMethod.type === 'ach') {
            const promoCode =
              context.funnelPacket?.user.signupPromoCode?.code ||
              context.funnelPacket?.user.promoCode;

            const propertiesData = {
              /* eslint-disable camelcase */
              payable_type: paymentMethod.type,
              payment_method_id: paymentMethod.id,
              /* eslint-disable camelcase */
            };
            await sendInstantAlert({
              utilityAccountId: context.funnelPacket.utilityAccount.id,
              event: 'payment_method_verified',
              properties: JSON.stringify(propertiesData),
            });

            if (allowConsolidatedUtilityAccount(promoCode)) {
              const funnelStatus = 'Payment Method Verified';
              const utilityAccountId = context.funnelPacket?.utilityAccount.id!;
              const updateInput = {
                funnelStatus,
                ipAddress: getUserIpFromStore(),
              };
              await updateConsolidatedUtilityAccount(utilityAccountId, updateInput);
            }
          }
        },
        saveExistingPayment: assign((context, event) => ({
          existingPaymentMethodId: event.data.existingPaymentMethodId,
          selectedPaymentMethod: context.existingPaymentMethods.find(
            p => p.id === event.data.existingPaymentMethodId,
          ),
        })),
        setInitialBillingSelection: assign(context => ({
          billingSelection: context.billingSelection || context.billingOptions[0],
        })),
        setInitialData: assign((_context, event) => ({
          existingPaymentMethods: event.data.existingPaymentMethods,
          stripeToken: event.data.stripeToken,
          placeCreditCardHold: event.data.placeCreditCardHold,
        })),
        setInitialDataError: assign((context, event) => ({
          errors: setError<BillingStates>('payment', context, event),
        })),
        selectOption: assign((_context, event) => ({
          billingSelection: event.data.billingSelection,
        })),
        setPaymentHoldResult: assign((_context, event) => ({
          paymentHoldResult: event.data.result,
        })),
        setPaymentType: assign((_context, event) => {
          let paymentType;
          if (event.data.type === 'credit_card') {
            paymentType = event.data.type;
          } else {
            paymentType = 'ach';
          }
          return { paymentType };
        }),
        setPaymentError: assign((context, event) => ({
          errors: setError<BillingStates>('payment', context, event),
        })),
        setPartialSelectedPaymentMethod: assign((_context, event) => ({
          selectedPaymentMethod: {
            lastFour: event.data.lastFour,
            source: event.data.source,
            type: event.data.type,
          },
        })),
        setSelectedPaymentMethod: assign((_context, event) => ({
          selectedPaymentMethod: event.data.paymentMethod,
        })),
        setUpdatedContextFromParent: assign((context, event) => ({
          ...context,
          ...event.data,
        })),
        trackCompleted: context => {
          sendToSegment({
            type: 'billing_step_completed',
            billingExperienceSelection: context.billingSelection!,
            funnelPacket: context.funnelPacket,
          });
        },
        trackStarted: context => {
          sendToSegment({
            type: 'billing_step_started',
            billingExperienceOptions: context.billingOptions,
            funnelPacket: context.funnelPacket,
          });
        },
        handlePaymentHoldError: context => {
          const payload = {
            extras: {
              userId: context.funnelPacket.user.id,
              paymentType: context.paymentType,
              errors: context.errors,
            },
          };
          trackError(new Error('PaymentHoldError'), payload);
        },
      },
      delays: {
        LISTEN_FOR_PAYMENT_TIMEOUT: 15000,
      },
      /* eslint-disable @typescript-eslint/no-unused-vars */
      guards: {
        paymentHoldFailed: (_, event) => event.data.result !== 'success',
        shouldSkipPayment: (_, event) => event.data.billingSelection === 'ucb',
        shouldPlaceHoldOnCreditCard: (context, event) =>
          context.placeCreditCardHold && event.data.type === 'credit_card',
        shouldPlaceHoldOnExistingCreditCard: (context, event) => {
          const { existingPaymentMethods, placeCreditCardHold } = context;
          const selected = existingPaymentMethods.find(
            p => p.id === event.data.existingPaymentMethodId,
          );
          return (
            placeCreditCardHold &&
            selected?.type === 'credit_card' &&
            !selected?.hasSuccessfulTransactions
          );
        },
      },
      services: {
        fetchInitialData: async context => {
          const accountId = context.funnelPacket.account.id;
          const [{ stripeToken }, { existingPaymentMethods }] = await Promise.all([
            fetchStripeToken({ accountId, allowMicroDeposits: context.allowMicroDeposits }),
            fetchExistingPaymentMethods({ accountId }),
          ]);
          const placeCreditCardHold = LDClient.variation(LDFlags.placeCreditCardHold);
          return { existingPaymentMethods, stripeToken, placeCreditCardHold };
        },
        listenForPayment: async context => {
          const { paymentMethod } = await listenForPayment({
            accountId: context.funnelPacket.account.id,
          });
          return { paymentMethod };
        },
        placeHold: async context => {
          const { funnelPacket, selectedPaymentMethod: payment } = context;
          const accountId = funnelPacket.account.id;
          const customPlanId = funnelPacket.customPlan.id;
          const paymentMethodId = payment && 'id' in payment && payment.id;
          if (!paymentMethodId) throw new Error('Missing payment id');

          const { result, message } = await createPaymentHold({
            accountId,
            paymentMethodId,
            deletePaymentMethodOnFailure: true,
          });

          sendToSegment({
            type: 'payment_hold_result',
            funnelPacket,
            paymentMethodId,
            result,
            message,
          });

          if (result === 'success') {
            await updatePlanPaymentMethod({ customPlanId, paymentMethodId });
            if (!context.existingPaymentMethodId) {
              sendToSegment({ type: 'payment_method_created', funnelPacket });
            }
          }

          return { result };
        },
        selectPayment: async context => {
          const { paymentMethod } = await updatePlanPaymentMethod({
            customPlanId: context.funnelPacket.customPlan.id,
            paymentMethodId: context.existingPaymentMethodId!,
          });
          return { paymentMethod };
        },
        trackPaymentSuccess: async context => {
          const promoCode =
            context.funnelPacket?.user.signupPromoCode?.code ||
            context.funnelPacket?.user.promoCode;
          const selectedPaymentMethod = context.selectedPaymentMethod as PaymentMethod;
          const paymentMethodId = selectedPaymentMethod?.id;
          const propertiesData = {
            /* eslint-disable camelcase */
            payable_type: context.paymentType,
            payment_method_id: paymentMethodId,
            /* eslint-disable camelcase */
          };

          await sendInstantAlert({
            utilityAccountId: context.funnelPacket.utilityAccount.id,
            event: 'payment_method_verified',
            properties: JSON.stringify(propertiesData),
          });

          if (allowConsolidatedUtilityAccount(promoCode)) {
            const funnelStatus = 'Payment Method Verified';
            const utilityAccountId = context.funnelPacket?.utilityAccount.id!;
            const updateInput = {
              funnelStatus,
              ipAddress: getUserIpFromStore(),
            };
            await updateConsolidatedUtilityAccount(utilityAccountId, updateInput);
          }
        },
      },
    },
  );

export const billingContext = contextCreator<BillingContext, 'funnelPacket'>(billingMachine);
